<script setup>
/**
 * FormControlUsersGroupsRolesCheckbox
 * Individual checkbox for a specific privilege.
 */
import {computed} from 'vue'
import {useUserRolesStore} from '@/stores/userRoles' // Adjust path to your userRoles store
import {useFormStore} from '@/stores/form'

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    rolesGroup: {
        type: String,
        required: true
    },
    component: {
        type: Object,
        required: true
    },
    option: {
        type: Object,
        required: true
    }
})

// Pinia Stores
const userRolesStore = useUserRolesStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// --- Computed ---

const privilegeName = computed(() => props.option.name)

const roleId = computed(() => formStore.value?.id)

/**
 * Determines if the checkbox should be visible.
 * Checks both the component's DB capabilities and the existence
 * of the role in the global config.
 */
const show = computed(() => {
    let optionEnabled = true

    switch (privilegeName.value) {
        case 'add':
            optionEnabled = !!props.component.addNew
            break
        case 'delete':
            optionEnabled = !!props.component.deleteOption
            break
        case 'archive':
            optionEnabled = !!props.component.useArchive
            break
    }

    if (!optionEnabled) return false

    // Verify the privilege exists in the roles schema
    const rolesConfig = userRolesStore.roles
    return !!rolesConfig?.[props.rolesGroup]?.[props.component.title]?.[privilegeName.value]
})

const privileges = computed(() => {
    return userRolesStore.privileges?.[roleId.value]?.[props.component.id]
})

const isChecked = computed(() => {
    return privileges.value?.includes(privilegeName.value)
})

// --- Methods ---

const setValue = () => {
    userRolesStore.togglePrivilege({
        roleId: roleId.value,
        componentId: props.component.id,
        privilege: privilegeName.value
    })
}
</script>

<template>
    <input
        v-if="show"
        :checked="isChecked"
        type="checkbox"
        @change="setValue"
    >
</template>